import React, {useEffect, useState} from 'react';

import {useMatchMedia} from '@udemy/hooks';
import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {useSiteStats, useUDData} from '@udemy/ud-data';

import {DynamicWebContentPageType, useValuePropsQuery} from '../../generated/graphql';
import {DemoFormValuePropsProps} from './demo-form-value-props.react-component';
import {RequestDemoStore} from './request-demo-store.mobx-store';
import {RequestDemo, RequestDemoProps} from './request-demo.react-component';

/**
 * Wrapper component for RequestDemoStore that is responsible for maintaining the store between renders and
 * initializing the Marketo form with the appropriate formId.
 * @constructor
 */
export const RequestDemoWrapper = () => {
    const udData = useUDData();
    const {getOrgNumericSiteStat} = useSiteStats();
    const {interpolate, gettext} = useI18n();
    const [requestDemoStore] = useState(new RequestDemoStore(udData));
    const featureListLoosePadding = useMatchMedia('xl-min');
    useEffect(() => {
        requestDemoStore.initializeMarketoForm('//info.udemy.com', '273-CKQ-053', udData);
    }, [requestDemoStore, udData]);
    const {data: cmsValuePropsData, isLoading: isLoadingValueProps} = useValuePropsQuery({
        pageType: DynamicWebContentPageType.RequestDemoForm,
    });
    const cmsValueProps = cmsValuePropsData?.dynamicWebContent;
    const cmsFeatureList = cmsValueProps?.contents
        ?.filter((content) => content?.heading)
        .map((content) => content?.heading as string)
        .map((feature, index) => <LocalizedHtml key={index} html={feature} interpolate={{}} />);

    const fallbackFeaturesList = [
        <LocalizedHtml
            key={'num_courses'}
            html={interpolate(
                gettext(
                    'Train your entire workforce with over %(num_courses)s courses in 15 languages',
                ),
                {
                    num_courses: getOrgNumericSiteStat('num_courses'),
                },
                true,
            )}
            interpolate={{}}
        />,
        <LocalizedHtml
            key={'certification_exams'}
            html={gettext('Prep employees for over 200 industry-recognized certification exams')}
            interpolate={{}}
        />,
        <LocalizedHtml
            key={'team_skills'}
            html={gettext('Develop highly skilled tech teams in risk-free practice environments')}
            interpolate={{}}
        />,
        <LocalizedHtml
            key={'skill_gaps'}
            html={gettext(
                'Identify emerging skills gaps, learning trends, and industry benchmarks',
            )}
            interpolate={{}}
        />,
        <LocalizedHtml
            key={'integrate_content'}
            html={gettext('Integrate content with your existing learning management system')}
            interpolate={{}}
        />,
    ];
    const features =
        cmsFeatureList && cmsFeatureList?.length > 0 ? cmsFeatureList : fallbackFeaturesList;

    const valuePropsProps: DemoFormValuePropsProps = {
        featureListLoosePadding: featureListLoosePadding || false,
        features,
    };
    const emptyStringFallback = (fallback: string, copy?: string | null) => {
        return copy && copy !== '' ? copy : fallback;
    };
    const hardcodedDemoFormProps: RequestDemoProps = {
        isLoadingValueProps,
        heading: emptyStringFallback(gettext('Get your demo'), cmsValueProps?.heading),
        subHeading: emptyStringFallback(
            gettext("Tell us your needs and we'll start on a custom plan to drive results."),
            cmsValueProps?.description,
        ),
        valuePropsHeading: emptyStringFallback(
            gettext('With Udemy as your learning partner, you can:'),
            cmsValueProps?.labelText,
        ),
        userType: 'visitor',
        stackedLayout: false,
        scrollToFirstInvalidField: false,
        valueProps: valuePropsProps,
        udData,
        requestDemoStore,
    };
    return <RequestDemo {...hardcodedDemoFormProps} />;
};
